import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import CompanyUserLayout from "../../layouts/CompanyUserLayout";
import CompanyAdminLayout from "../../layouts/CompanyAdminLayout";
import { useAuth } from "../../context/AuthContext";
import { getTokenFromCookie } from "../../api/services/authService";
import { useToast } from "../../components/ToastContext";
import AttackBoxLaunchModal from "../../components/lab/AttackBoxLaunchModal";
import ChatWidget from "../../components/ChatWidget";

import {
  provisionAttackBox,
  requestVpnCertificate,
  getProvisioningStatus,
  getLatestProvisioningStatus,
  getTeamScenario,
  submitTeamAnswer,
  stopTeamScenario,
  deleteAttackBox,
  getTeamAnswersStreamUrl,
} from "../../api/services/labService";

import { hasPermission } from "../../utils/permissions";
import {
  Play,
  BookOpen,
  FileText,
  Monitor,
  Download,
  Square,
  ExternalLink,
  ShieldAlert,
  ShieldOff,
  ArrowLeft,
  Info,
  Sparkles,
  Trophy,
  BarChart3,
  X,
} from "lucide-react";
import TeamLabQuestionsSection from "../CompanyUser/TeamLabQuestionSection";
import LabWalkthroughTab from "../../components/lab/LabWalkthroughTab";
import LabRemediationTab from "../../components/lab/LabRemediationTab";

// ─── constants ──

const ACTIVE_PROVISION_STATES = new Set(["queued", "in_progress"]);
const POLL_INTERVAL_MS = 3000;
const ATTACKBOX_DIRECT_TIMEOUT_MS = 180000;
const ATTACKBOX_PENDING_MESSAGE =
  "Machine running, preparing secure launch link...";
const ATTACKBOX_TIMEOUT_MESSAGE =
  "Secure launch link could not be generated. Verify AttackBox RAM role and credential upload path.";

const ATTACKBOX_UI_PHASE = Object.freeze({
  IDLE: "idle",
  STARTING: "starting",
  PENDING: "pending",
  READY: "ready",
  ERROR: "error",
});

// ─── helpers ────

function parseIsoTimestamp(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function hasUnexpiredDirectLaunch(result) {
  const urlType = (result?.url_type || "").toLowerCase();
  if (
    urlType !== "direct" ||
    !result?.guacamole_url ||
    !result?.url_expires_at
  ) {
    return false;
  }
  const expiresAt = parseIsoTimestamp(result.url_expires_at);
  return Boolean(expiresAt && expiresAt.getTime() > Date.now());
}

function extractScenarioIdFromUrl(scenarioUrl) {
  if (typeof scenarioUrl !== "string") return null;
  const match = scenarioUrl.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  );
  return match ? match[0] : null;
}

function parseSseChunk(chunk) {
  const frames = chunk.split("\n\n").filter(Boolean);
  const events = [];
  for (const frame of frames) {
    let event = "message";
    let data = "";
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) {
        event = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        data = line.slice(5).trim();
      }
    }
    if (data) events.push({ event, data });
  }
  return events;
}

// ─── SSE debug ────
// Set to true to log SSE lifecycle events to the console.
const SSE_DEBUG = false;
const sseLog = (...args) => SSE_DEBUG && console.log("[SSE]", ...args);

// ─── component ──

export default function TeamLabPage() {
  const { labId } = useParams();
  const location = useLocation();
  const preloadedLabData = location.state;
  const teamId = preloadedLabData?.team_id ?? null;

  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const canControlTeamScenario = hasPermission(user, "team_scenario_control");

  // Admin = read-only monitoring view. Adjust this line to match however your
  const isAdmin = user?.roles?.includes("company_admin") ?? false;
  const readOnly = isAdmin;

  // Swap layout based on role
  const PageLayout = isAdmin ? CompanyAdminLayout : CompanyUserLayout;

  // ── team scenario ─────────
  const [teamScenario, setTeamScenario] = useState(null);
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);
  const [hasScenarioResponse, setHasScenarioResponse] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isScenarioGone, setIsScenarioGone] = useState(false);

  // ── question / answer state ─────────
  const [answers, setAnswers] = useState({});
  // submitted[idx] = "correct" | "incorrect" | "error"
  const [submitted, setSubmitted] = useState({});
  // submittingQuestions[idx] = true while POST /answers is in-flight
  const [submittingQuestions, setSubmittingQuestions] = useState({});
  const [attemptedQuestions, setAttemptedQuestions] = useState({});
  // attempts[idx] = number of wrong submissions — lives here so SSE re-renders
  // of questions[] don't wipe the count. Reset only when labId changes.
  const [attempts, setAttempts] = useState({});

  // questionAnswerData[idx] = { answer_text, submitted_by_username, submitted_at }
  // Populated from POST response (submitter) or GET resync (teammate).
  const [questionAnswerData, setQuestionAnswerData] = useState({});

  // ── attack-box state ──────
  const [isStartingAttackBox, setIsStartingAttackBox] = useState(false);
  const [isAttackBoxProvisioning, setIsAttackBoxProvisioning] = useState(false);
  const [attackBoxRequestId, setAttackBoxRequestId] = useState(null);
  const [attackBoxUrl, setAttackBoxUrl] = useState(null);
  const [attackBoxUrlExpiresAt, setAttackBoxUrlExpiresAt] = useState(null);
  const [attackBoxLastError, setAttackBoxLastError] = useState(null);
  const attackBoxPendingSinceRef = useRef(null);
  const secureLaunchToastShownRef = useRef(false);
  const attackBoxPollGenerationRef = useRef(0);
  const [showAttackBoxLaunchModal, setShowAttackBoxLaunchModal] =
    useState(false);

  // ── VPN state ──
  const [isDownloadingVpn, setIsDownloadingVpn] = useState(false);

  // ── team-challenge state ──
  const [isTeamChallengeLoading, setIsTeamChallengeLoading] = useState(false);
  const [isTeamChallengeStopped, setIsTeamChallengeStopped] = useState(false);

  // ── misc UI ──
  const [activeTab, setActiveTab] = useState("play");
  const [isStoppingAttackBox, setIsStoppingAttackBox] = useState(false);
  const walkthroughRef = useRef(null);
  const completionToastShownRef = useRef(false);

  // ── derived scenario data ──
  const scenario = teamScenario?.scenario ?? null;
  const chatScenarioId = useMemo(
    () => extractScenarioIdFromUrl(scenario?.scenario_url),
    [scenario?.scenario_url],
  );
  const rawMetaQuestions = scenario?.metadata?.questions ?? [];
  const rawTopLevelQuestions = teamScenario?.questions ?? [];

  const privateIp = teamScenario?.provisioning?.private_ip ?? null;

  // Merge metadata questions (rich hint/context) with top-level answer state.
  // Overlay questionAnswerData so POST/resync-revealed fields are reflected.
  const questions = useMemo(() => {
    const base =
      rawMetaQuestions.length > 0
        ? rawMetaQuestions.map((mq, idx) => {
            const tq = rawTopLevelQuestions[idx] ?? {};
            return {
              ...mq,
              is_answered: tq.is_answered ?? false,
              locked: tq.locked ?? false,
              answer_text: tq.answer_text ?? null,
              submitted_by_username: tq.submitted_by_username ?? null,
              submitted_at: tq.submitted_at ?? null,
              answer_length: tq.answer_length ?? null,
            };
          })
        : rawTopLevelQuestions;

    // Overlay live answer data (from POST response or resync GET)
    return base.map((q, idx) => {
      const live = questionAnswerData[idx];
      if (!live) return q;
      return {
        ...q,
        locked: true,
        is_answered: true,
        answer_text: live.answer_text ?? q.answer_text,
        submitted_by_username:
          live.submitted_by_username ?? q.submitted_by_username,
        submitted_at: live.submitted_at ?? q.submitted_at,
      };
    });
  }, [rawMetaQuestions, rawTopLevelQuestions, questionAnswerData]);

  const runbookMarkdown = scenario?.metadata?.runbook ?? "";
  const playbookMarkdown = scenario?.metadata?.playbook ?? "";
  const walkthroughMarkdown = scenario?.metadata?.walkthrough_markdown ?? "";
  // ── TKSA aggregation from per-question framework_alignment ──
  const tksaBuckets = useMemo(() => {
    // Strip any trailing description from raw TKSA values so the panel shows
    // just the ID, e.g. "T4507: Carry out vulnerability scanning..." → "T4507".
    const TKSA_ID_REGEX = /^([TKSA])(\d+)/i;
    const cleanId = (raw) => {
      if (typeof raw !== "string") return raw;
      const m = raw.trim().match(TKSA_ID_REGEX);
      return m ? `${m[1].toUpperCase()}${m[2]}` : raw.trim();
    };

    const sets = {
      Tasks: new Set(),
      Skills: new Set(),
      Abilities: new Set(),
      Knowledge: new Set(),
      Other: new Set(),
    };

    questions.forEach((q) => {
      (q.framework_alignment?.tksa_ids || []).forEach((raw) => {
        const id = cleanId(raw);
        const prefix = id?.[0]?.toUpperCase();
        if (prefix === "T") sets.Tasks.add(id);
        else if (prefix === "S") sets.Skills.add(id);
        else if (prefix === "A") sets.Abilities.add(id);
        else if (prefix === "K") sets.Knowledge.add(id);
        else sets.Other.add(id);
      });
    });

    return Object.entries(sets)
      .map(([label, set]) => ({ label, items: [...set].sort() }))
      .filter((b) => b.items.length > 0);
  }, [questions]);

  const mitreAttack = scenario?.metadata?.mitre_attack || null;
  const hasTksaData = tksaBuckets.length > 0;
  const hasMitreData =
    !!mitreAttack &&
    Object.values(mitreAttack).some((v) => Array.isArray(v) && v.length > 0);
  const [frameworkTab, setFrameworkTab] = useState("tksa");
  const [mappingsModalOpen, setMappingsModalOpen] = useState(false);
  // Fall back to whichever tab actually has data so the card never renders empty.
  const activeFrameworkTab =
    frameworkTab === "mitre"
      ? hasMitreData
        ? "mitre"
        : "tksa"
      : hasTksaData
        ? "tksa"
        : "mitre";

  const labTitle =
    scenario?.title ?? preloadedLabData?.title ?? "Lab not found";
  const labDescription =
    scenario?.description ?? preloadedLabData?.description ?? "";

  // ── resync helper ──
  // Called on initial load and after SSE reconnects.
  // Merges answered questions from the GET response into questionAnswerData.
  const resyncFromServer = useCallback(
    async (silent = false) => {
      if (!labId) return;
      try {
        const data = await getTeamScenario(labId, teamId);
        setTeamScenario(data);

        // Build answer data map from answered questions
        const newAnswerData = {};
        (data.questions ?? []).forEach((q) => {
          if (q.is_answered || q.locked) {
            newAnswerData[q.question_index] = {
              answer_text: q.answer_text,
              submitted_by_username: q.submitted_by_username,
              submitted_at: q.submitted_at,
            };
          }
        });

        setQuestionAnswerData((prev) => ({ ...prev, ...newAnswerData }));

        // Mark already-answered questions as correct in submitted map
        setSubmitted((prev) => {
          const next = { ...prev };
          Object.keys(newAnswerData).forEach((idx) => {
            if (!next[idx]) next[idx] = "correct";
          });
          return next;
        });

        // Unlock walkthrough for questions answered by any teammate
        setAttemptedQuestions((prev) => {
          const next = { ...prev };
          Object.keys(newAnswerData).forEach((idx) => {
            next[idx] = true;
          });
          return next;
        });

        return data;
      } catch (err) {
        console.error("Failed to resync team scenario:", err);
      }
    },
    [labId, teamId],
  );

  // ── initial load ───
  useEffect(() => {
    if (!labId) return;
    let isActive = true;

    const load = async () => {
      try {
        setHasScenarioResponse(false);
        setIsLoadingScenario(true);
        setLoadError("");

        const data = await getTeamScenario(labId, teamId);
        if (!isActive) return;

        setTeamScenario(data);

        // Seed answer data from GET response (point 3)
        const initialAnswerData = {};
        (data.questions ?? []).forEach((q) => {
          if (q.is_answered || q.locked) {
            initialAnswerData[q.question_index] = {
              answer_text: q.answer_text,
              submitted_by_username: q.submitted_by_username,
              submitted_at: q.submitted_at,
            };
          }
        });
        setQuestionAnswerData(initialAnswerData);

        // Seed submitted map so answered questions show "correct" on load
        const initialSubmitted = {};
        Object.keys(initialAnswerData).forEach((idx) => {
          initialSubmitted[idx] = "correct";
        });
        setSubmitted(initialSubmitted);

        // Seed attemptedQuestions so walkthrough unlocks if all already answered
        const initialAttempted = {};
        Object.keys(initialAnswerData).forEach((idx) => {
          initialAttempted[idx] = true;
        });

        setAnswers({});
        setSubmittingQuestions({});
        setAttemptedQuestions(initialAttempted);
        setAttempts({});
        setHasScenarioResponse(true);
      } catch (err) {
        console.error("Failed to load team scenario:", err);
        if (isActive) {
          if (err?.status === 404 || err?.response?.status === 404) {
            setIsScenarioGone(true);
          } else {
            setLoadError(err?.message ?? "Failed to load scenario details.");
          }
          setHasScenarioResponse(true);
        }
      } finally {
        if (isActive) setIsLoadingScenario(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [labId]);

  // ── SSE stream via fetch (supports Authorization header) ───

  useEffect(() => {
    if (!labId) return;

    const streamUrl = getTeamAnswersStreamUrl({
          scenarioId: labId,
          teamId,
        });
    let reconnectTimerId = null;
    let stopped = false; // set true on unmount to prevent reconnects
    let currentAbortController = null;

    const startStream = async () => {
      // Each attempt gets its own AbortController so React StrictMode
      // double-invoke doesn't abort a live connection
      currentAbortController = new AbortController();

      // Read token fresh on every connect so token refreshes are picked up
      const accessToken = getTokenFromCookie("access_token") ?? "";

      sseLog("connecting, token present:", !!accessToken);
      if (stopped) return;
      try {
        const response = await fetch(streamUrl, {
          headers: {
            Accept: "text/event-stream",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          signal: currentAbortController.signal,
        });

        sseLog("response status:", response.status);
        if (!response.ok) {
          if (response.status === 404) {
            // Scenario no longer exists — show stopped screen, stop reconnecting
            setIsScenarioGone(true);
            stopped = true;
            return;
          }
          // 401/403 — token may have just refreshed, retry after delay
          if (response.status === 401 || response.status === 403) {
            if (!stopped) {
              reconnectTimerId = setTimeout(startStream, 5000);
            }
            return;
          }
          throw new Error(`SSE response ${response.status}`);
        }

        const reader = response.body
          .pipeThrough(new TextDecoderStream())
          .getReader();

        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            sseLog("stream ended — will reconnect");
            break;
          }

          sseLog("chunk received:", JSON.stringify(value.slice(0, 120)));
          buffer += value;

          // Process all complete SSE frames (terminated by \n\n)
          const boundary = buffer.lastIndexOf("\n\n");
          if (boundary === -1) continue;

          const complete = buffer.slice(0, boundary + 2);
          buffer = buffer.slice(boundary + 2);

          const sseEvents = parseSseChunk(complete);
          sseLog(
            "parsed events:",
            sseEvents.map((e) => e.event),
          );

          for (const { event, data } of sseEvents) {
            if (event === "keepalive") continue;

            if (event === "answer_locked") {
              try {
                const payload = JSON.parse(data);
                const idx = payload?.question_index;
                if (typeof idx !== "number") continue;

                // Immediately show locked state (point 5)
                setQuestionAnswerData((prev) => ({
                  ...prev,
                  [idx]: {
                    answer_text: prev[idx]?.answer_text ?? null, // will be filled by resync
                    submitted_by_username:
                      payload.submitted_by_username ?? null,
                    submitted_at: payload.submitted_at ?? null,
                  },
                }));
                setSubmitted((prev) =>
                  prev[idx] === "correct"
                    ? prev
                    : { ...prev, [idx]: "correct" },
                );

                // Fetch revealed answer_text for this question (point 5)
                resyncFromServer(true);

                // Toast for teammate answers
                if (
                  payload.submitted_by_username &&
                  payload.submitted_by_username !== user?.username
                ) {
                  showToast(
                    `Question ${idx + 1} answered correctly by ${payload.submitted_by_username}`,
                    "info",
                  );
                }
              } catch {
                // malformed payload — ignore
              }
            }
          }
        }

        // Backend closed the stream (done=true) — reconnect after short delay
        if (!stopped) {
          sseLog("server closed stream, reconnecting in 3s...");
          reconnectTimerId = setTimeout(startStream, 3000);
        }
      } catch (err) {
        if (err?.name === "AbortError") return; // intentional teardown — do not reconnect

        // Unexpected error — resync state then reconnect (point 6)
        resyncFromServer(true);

        if (!stopped) {
          reconnectTimerId = setTimeout(startStream, 3000);
        }
      }
    };

    startStream();

    return () => {
      stopped = true;
      currentAbortController?.abort();
      if (reconnectTimerId) clearTimeout(reconnectTimerId);
    };
  }, [labId, resyncFromServer, showToast]);
  // Token is read from cookie inside the effect on every (re)connect so it
  // always picks up the latest value after a token refresh.

  // ── Poll only for stopped/expired scenario detection ─────
  // SSE handles answer syncing. This only checks if the scenario is gone (404).
  useEffect(() => {
    if (!labId || isScenarioGone) return;

    const checkIfGone = async () => {
      try {
        await getTeamScenario(labId, teamId);
      } catch (err) {
        if (err?.status === 404 || err?.response?.status === 404) {
          setIsScenarioGone(true);
        }
      }
    };

    const id = setInterval(checkIfGone, 30000);
    return () => clearInterval(id);
  }, [labId, isScenarioGone]);

  // ── hydrate attack-box status on mount ───────
  useEffect(() => {
    if (!user?.username) return;
    let isActive = true;

    const hydrate = async () => {
      try {
        const attackBoxStatus = await getLatestProvisioningStatus({
          resourceType: "attackbox",
          username: user.username,
          resourceKey: user.username,
        });
        if (!isActive) return;

        if (
          attackBoxStatus.state === "ready" &&
          hasUnexpiredDirectLaunch(attackBoxStatus.result)
        ) {
          setAttackBoxUrl(attackBoxStatus.result.guacamole_url);
          setAttackBoxUrlExpiresAt(attackBoxStatus.result.url_expires_at);
        } else if (ACTIVE_PROVISION_STATES.has(attackBoxStatus.state)) {
          setAttackBoxRequestId(attackBoxStatus.request_id);
          setIsAttackBoxProvisioning(true);
          if (!attackBoxPendingSinceRef.current) {
            attackBoxPendingSinceRef.current = Date.now();
          }
        }
      } catch (error) {
        if (error?.status !== 404) {
          console.warn("Failed loading attack box status:", error);
        }
      }
    };

    hydrate();
    return () => {
      isActive = false;
    };
  }, [user?.username]);

  // ── poll attack-box provisioning ────
  useEffect(() => {
    if (!attackBoxRequestId) return;

    let active = true;
    const generation = ++attackBoxPollGenerationRef.current;
    let timerId = null;

    const scheduleNext = () => {
      if (!active || attackBoxPollGenerationRef.current !== generation) return;
      timerId = setTimeout(pollAttackBoxStatus, POLL_INTERVAL_MS);
    };

    if (!attackBoxPendingSinceRef.current) {
      attackBoxPendingSinceRef.current = Date.now();
    }

    const pollAttackBoxStatus = async () => {
      try {
        const status = await getProvisioningStatus({
          requestId: attackBoxRequestId,
        });
        if (!active || attackBoxPollGenerationRef.current !== generation)
          return;

        const timedOut =
          Date.now() - (attackBoxPendingSinceRef.current || Date.now()) >
          ATTACKBOX_DIRECT_TIMEOUT_MS;

        if (status.state === "ready") {
          if (hasUnexpiredDirectLaunch(status.result)) {
            setAttackBoxUrl(status.result.guacamole_url);
            setAttackBoxUrlExpiresAt(status.result.url_expires_at);
            setIsAttackBoxProvisioning(false);
            setAttackBoxRequestId(null);
            setAttackBoxLastError(null);
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
            showToast("Attack box provisioned successfully.", "success");
            return;
          }
          setIsAttackBoxProvisioning(true);
          if (timedOut) {
            setIsAttackBoxProvisioning(false);
            setAttackBoxRequestId(null);
            setAttackBoxLastError(
              status.error_message || ATTACKBOX_TIMEOUT_MESSAGE,
            );
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
            showToast(
              status.error_message || ATTACKBOX_TIMEOUT_MESSAGE,
              "error",
            );
            return;
          }
          if (!secureLaunchToastShownRef.current) {
            secureLaunchToastShownRef.current = true;
            showToast(ATTACKBOX_PENDING_MESSAGE, "info");
          }
          scheduleNext();
          return;
        }

        if (status.state === "failed" || status.state === "cancelled") {
          setIsAttackBoxProvisioning(false);
          setAttackBoxRequestId(null);
          setAttackBoxLastError(
            status.error_message || "Failed to initialize secure launch link.",
          );
          attackBoxPendingSinceRef.current = null;
          secureLaunchToastShownRef.current = false;
          showToast(
            status.error_message || "Failed to initialize secure launch link.",
            "error",
          );
          return;
        }

        if (ACTIVE_PROVISION_STATES.has(status.state)) {
          setIsAttackBoxProvisioning(true);
          if (timedOut) {
            setIsAttackBoxProvisioning(false);
            setAttackBoxRequestId(null);
            setAttackBoxLastError(ATTACKBOX_TIMEOUT_MESSAGE);
            attackBoxPendingSinceRef.current = null;
            secureLaunchToastShownRef.current = false;
            showToast(ATTACKBOX_TIMEOUT_MESSAGE, "error");
            return;
          }
          scheduleNext();
          return;
        }
      } catch (error) {
        if (!active || attackBoxPollGenerationRef.current !== generation)
          return;
        if (error?.status === 404) {
          setIsAttackBoxProvisioning(false);
          setAttackBoxRequestId(null);
          setAttackBoxLastError(null);
          attackBoxPendingSinceRef.current = null;
          return;
        }
        setAttackBoxLastError(
          error.message || "Failed to refresh attack box status.",
        );
        showToast(
          error.message || "Failed to refresh attack box status.",
          "error",
        );
        scheduleNext();
        return;
      }
      scheduleNext();
    };

    pollAttackBoxStatus();
    return () => {
      active = false;
      if (timerId) clearTimeout(timerId);
    };
  }, [attackBoxRequestId, showToast]);

  // ── attack-box derived UI ──
  const directAttackBoxReady = Boolean(
    attackBoxUrl &&
    attackBoxUrlExpiresAt &&
    parseIsoTimestamp(attackBoxUrlExpiresAt)?.getTime() > Date.now(),
  );

  const attackBoxUiPhase = useMemo(() => {
    if (directAttackBoxReady) return ATTACKBOX_UI_PHASE.READY;
    if (isStartingAttackBox) return ATTACKBOX_UI_PHASE.STARTING;
    if (isAttackBoxProvisioning) return ATTACKBOX_UI_PHASE.PENDING;
    if (attackBoxLastError) return ATTACKBOX_UI_PHASE.ERROR;
    return ATTACKBOX_UI_PHASE.IDLE;
  }, [
    directAttackBoxReady,
    isStartingAttackBox,
    isAttackBoxProvisioning,
    attackBoxLastError,
  ]);

  const attackBoxBusy =
    attackBoxUiPhase === ATTACKBOX_UI_PHASE.STARTING ||
    attackBoxUiPhase === ATTACKBOX_UI_PHASE.PENDING;

  const attackBoxButtonLabel = {
    [ATTACKBOX_UI_PHASE.READY]: "Open in Browser",
    [ATTACKBOX_UI_PHASE.STARTING]: "Starting...",
    [ATTACKBOX_UI_PHASE.PENDING]: "Preparing secure link...",
    [ATTACKBOX_UI_PHASE.ERROR]: "Retry Start Attack Box",
    [ATTACKBOX_UI_PHASE.IDLE]: "Start Attack Box",
  }[attackBoxUiPhase];

  const attackBoxInlineMessage = {
    [ATTACKBOX_UI_PHASE.PENDING]: ATTACKBOX_PENDING_MESSAGE,
    [ATTACKBOX_UI_PHASE.ERROR]: attackBoxLastError,
    [ATTACKBOX_UI_PHASE.IDLE]:
      "Start Attack Box to generate a secure browser launch link.",
  }[attackBoxUiPhase];

  // ── answer submission (point 4) ──────
  //
  // POST /api/v1/team-scenario/{scenarioId}/answers
  // On is_correct=true: immediately update UI with POST response fields
  //   (answer_text, submitted_by_username, submitted_at).
  // On 409: already locked by teammate — resync to get answer_text.
  const handleSubmitAnswer = useCallback(
    async (idx, _q) => {
      if (readOnly) return false;
      const answerText = (answers[idx] ?? "").trim();
      if (!answerText) return false;

      setSubmittingQuestions((prev) => ({ ...prev, [idx]: true }));

      try {
        const result = await submitTeamAnswer({
          scenarioId: labId,
          questionIndex: idx,
          answerText,
        });

        const isCorrect = result?.is_correct === true;

        setSubmitted((prev) => ({
          ...prev,
          [idx]: isCorrect ? "correct" : "incorrect",
        }));

        if (isCorrect) {
          // Point 4: use POST response fields directly — no extra GET needed
          setQuestionAnswerData((prev) => ({
            ...prev,
            [idx]: {
              answer_text: result.answer_text ?? answerText,
              submitted_by_username: result.submitted_by_username ?? null,
              submitted_at: result.submitted_at ?? null,
            },
          }));
          setAttemptedQuestions((prev) =>
            prev[idx] ? prev : { ...prev, [idx]: true },
          );
        } else {
          // Track wrong attempts so the hint threshold is preserved across
          // SSE-driven re-renders of the questions array
          setAttempts((prev) => ({ ...prev, [idx]: (prev[idx] ?? 0) + 1 }));
        }

        return isCorrect;
      } catch (err) {
        // 409 = already locked by teammate — resync to reveal answer_text
        if (err?.status === 409) {
          setSubmitted((prev) => ({ ...prev, [idx]: "correct" }));
          resyncFromServer(true);
          return true;
        }

        setSubmitted((prev) => ({ ...prev, [idx]: "error" }));
        showToast(
          err?.message ?? "Failed to submit answer. Please try again.",
          "error",
        );
        return false;
      } finally {
        setSubmittingQuestions((prev) => ({ ...prev, [idx]: false }));
      }
    },
    [answers, labId, resyncFromServer, showToast],
  );

  // ── other handlers ─────────
  const handleStartAttackBox = async () => {
    if (!user?.username) {
      showToast("Please log in to start the attack box.", "warning");
      return;
    }
    try {
      setIsStartingAttackBox(true);
      setAttackBoxUrl(null);
      setAttackBoxUrlExpiresAt(null);
      setAttackBoxLastError(null);
      attackBoxPendingSinceRef.current = Date.now();
      secureLaunchToastShownRef.current = false;
      const response = await provisionAttackBox({
        autoTerminateHours: 4,
        instanceType: "ecs.t6-c1m2.large",
        username: user.username,
      });
      if (!response?.request_id) {
        throw new Error("Provisioning request ID missing in response.");
      }
      setAttackBoxRequestId(response.request_id);
      setIsAttackBoxProvisioning(true);
      showToast(ATTACKBOX_PENDING_MESSAGE, "info");
    } catch (error) {
      setAttackBoxLastError(error.message || "Failed to start attack box.");
      attackBoxPendingSinceRef.current = null;
      showToast(error.message || "Failed to start attack box.", "error");
    } finally {
      setIsStartingAttackBox(false);
    }
  };

  const handleOpenAttackBox = () => {
    if (!attackBoxUrl) {
      showToast("Attack box URL not available.", "error");
      return;
    }
    setShowAttackBoxLaunchModal(true);
  };

  const handleLaunchAttackBoxUrl = () => {
    if (!attackBoxUrl) {
      showToast("Attack box URL not available.", "error");
      return;
    }
    window.open(attackBoxUrl, "_blank");
  };

  const handleStopAttackBox = async () => {
    if (!user?.username) {
      showToast("Please log in to stop the attack box.", "warning");
      return;
    }
    try {
      setIsStoppingAttackBox(true);
      await deleteAttackBox({ username: user.username });
      setAttackBoxUrl(null);
      setAttackBoxUrlExpiresAt(null);
      setAttackBoxRequestId(null);
      setIsAttackBoxProvisioning(false);
      setAttackBoxLastError(null);
      attackBoxPendingSinceRef.current = null;
      secureLaunchToastShownRef.current = false;
      showToast("Attack box stopped successfully.", "success");
    } catch (error) {
      showToast(error.message || "Failed to stop attack box.", "error");
    } finally {
      setIsStoppingAttackBox(false);
    }
  };

  const handleDownloadVpn = async () => {
    if (!user?.username) {
      showToast("Please log in to request a VPN certificate.", "warning");
      return;
    }
    try {
      setIsDownloadingVpn(true);
      const response = await requestVpnCertificate({
        protocol: "udp",
        username: user.username,
      });
      if (!response?.download_url) {
        showToast("Download URL missing in response.", "error");
        return;
      }
      const anchor = document.createElement("a");
      anchor.href = response.download_url;
      anchor.download =
        response.certificate_path?.split("/").pop() || "vpn-config.ovpn";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      showToast("VPN certificate download started.", "success");
    } catch (error) {
      showToast(
        error.message || "Failed to download VPN certificate.",
        "error",
      );
    } finally {
      setIsDownloadingVpn(false);
    }
  };

  const handleStopTeamChallenge = async () => {
    if (!labId) {
      showToast("Scenario ID is missing.", "error");
      return;
    }
    try {
      setIsTeamChallengeLoading(true);
      await stopTeamScenario({ scenarioId: labId, teamId }); // ← add teamId

      setIsTeamChallengeStopped(true);
      showToast("Team challenge stopped.", "success");
    } catch (error) {
      showToast(error.message || "Failed to stop team challenge.", "error");
    } finally {
      setIsTeamChallengeLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!walkthroughRef.current) return;
    const content = walkthroughRef.current.innerHTML;
    const title = labTitle;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <title>${title} - Walkthrough</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; line-height: 1.6; color: #1a1a1a; padding: 40px 48px; max-width: 860px; margin: 0 auto; }
            h1 { font-size: 22px; font-weight: 700; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 2px solid #e5e7eb; color: #111; }
            h2 { font-size: 17px; font-weight: 700; margin-top: 28px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; color: #111; }
            p { margin: 6px 0; color: #374151; }
            code { background: #f0fdf4; color: #15803d; padding: 1px 5px; border-radius: 4px; font-size: 11px; font-family: 'Courier New', monospace; }
            pre { background: #1a1a1a; color: #f3f4f6; padding: 14px 16px; border-radius: 8px; overflow-x: auto; margin: 10px 0; }
            pre code { background: none; color: inherit; padding: 0; }
            ul, ol { padding-left: 22px; margin: 6px 0; color: #374151; }
            @media print { body { padding: 20px; } pre { white-space: pre-wrap; word-break: break-all; } }
          </style>
        </head>
        <body>
          <h1 style="border-bottom:2px solid #16a34a">${title} — Walkthrough</h1>
          ${content}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // ── derived display flags ──
  const totalQuestions = questions.length;
  const showQuestionsLoader =
    isLoadingScenario || (!hasScenarioResponse && !loadError);
  const allQuestionsAttempted =
    totalQuestions > 0 &&
    Object.keys(attemptedQuestions).length >= totalQuestions;
  const allQuestionsAnswered =
    totalQuestions > 0 &&
    questions.every((question) => question.is_answered || question.locked);
  const showChallengeCompleted =
    hasScenarioResponse && !isLoadingScenario && allQuestionsAnswered;

  const tabs = [
    { id: "play", label: "Play Machine", icon: Play },
    { id: "walkthroughs", label: "Walkthroughs", icon: BookOpen },
    { id: "remediation", label: "Response and Remediation", icon: FileText },
  ];

  useEffect(() => {
    completionToastShownRef.current = false;
  }, [labId]);

  useEffect(() => {
    if (!showChallengeCompleted || completionToastShownRef.current) return;
    completionToastShownRef.current = true;
    showToast("Challenge completed.", "success", 10000);
  }, [showChallengeCompleted, showToast]);

  // ── render ──────

  // Challenge no longer exists (404) — stopped or expired
  if (isScenarioGone) {
    return (
      <PageLayout>
        <div className="w-full max-w-362.5 mx-auto px-3 sm:px-6 py-6 sm:py-10 min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 text-center max-w-md">
            {/* Icon */}
            <div className="flex h-16 w-16 items-center justify-center rounded-md border border-(--color-border) bg-(--color-bg)">
              <ShieldOff className="h-7 w-7 text-(--color-text-muted)" />
            </div>
            {/* Text */}
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-(--color-text-primary)">
                This challenge is no longer active
              </h2>
              <p className="text-sm text-(--color-text-muted) leading-relaxed">
                The team challenge has been stopped or has expired. Head back to
                see other active challenges and collaborate with your team.
              </p>
            </div>
            {/* CTA */}
            <button
              type="button"
              onClick={() =>
                navigate(isAdmin ? "/company-admin/team-challenges" : "/my-team")
              }
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-(--color-primary) bg-(--color-primary) text-(--color-text-on-primary) text-sm font-semibold transition-all hover:opacity-90"
            >
              <ArrowLeft className="h-4 w-4" />
              {isAdmin ? "Back to Team Challenges" : "Back to My Team"}
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="w-full max-w-362.5 mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-6 min-h-[calc(100vh-4rem)]">
        <div className="space-y-4">
          <div className="w-full">
            <p className="text-xs uppercase tracking-[0.25em] text-(--color-primary)/80">
              Team Challenge
            </p>
            {readOnly && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-(--color-border) bg-transparent text-xs text-(--color-text-muted)">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Read-only monitoring view — actions are disabled for admins.
              </div>
            )}
            {isLoadingScenario && !teamScenario && !preloadedLabData ? (
              <div className="space-y-3 mt-1">
                <div className="h-9 bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-md animate-pulse bg-size-[200%_100%] w-96 max-w-full" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4 flex-wrap mt-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-(--color-text-primary) leading-tight [text-shadow:0_0_12px_var(--color-shadow-primary)]/30">
                    {labTitle}
                  </h1>
                  {!isLoadingScenario &&
                    hasScenarioResponse &&
                    (hasTksaData || hasMitreData) && (
                      <button
                        type="button"
                        onClick={() => setMappingsModalOpen(true)}
                        className="shrink-0 inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-(--color-primary)/5 px-4 py-2 text-xs sm:text-sm font-semibold text-(--color-primary) hover:bg-(--color-primary)/10 hover:shadow-[0_0_15px_rgba(0,255,0,0.2)] transition-all"
                      >
                        <BarChart3 className="w-4 h-4" />
                        View TKSA Alignment and MITRE Mapping
                      </button>
                    )}
                  {(isLoadingScenario ||
                    (!hasScenarioResponse && !loadError)) && (
                    <div className="shrink-0 h-9 w-64 max-w-full rounded-md bg-linear-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 animate-pulse bg-size-[200%_100%]" />
                  )}
                </div>
                <p className="text-(--color-text-muted) dark:text-gray-400 text-sm max-w-3xl mt-3">
                  Team challenge environment. Connect using VPN or Attack Box to
                  participate.
                </p>
                {showChallengeCompleted && (
                  <div
                    className="mt-5 relative overflow-hidden rounded-md"
                    style={{
                      background: "var(--color-bg-dark-card-alt-2)",
                      border: "1px solid var(--color-border-medium)",
                      padding: "18px 22px 18px 24px",
                    }}
                  >
                    {/* scanline texture */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(0deg, transparent, transparent 23px, rgba(0,255,0,0.02) 23px, rgba(0,255,0,0.02) 24px)",
                      }}
                    />

                    {/* top edge glow line */}
                    <div
                      className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent 0%, var(--color-border-medium) 20%, var(--color-border-dark) 50%, var(--color-border-medium) 80%, transparent 100%)",
                      }}
                    />

                    {/* left accent bar */}
                    <div
                      className="absolute left-0 top-[10%] bottom-[10%] w-0.75 rounded-sm pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(180deg, transparent, var(--color-primary) 30%, var(--color-primary) 70%, transparent)",
                      }}
                    />

                    {/* content */}
                    <div className="relative flex flex-wrap items-center justify-between gap-3">
                      {/* left: icon + text */}
                      <div className="flex items-center gap-3.5">
                        <div
                          className="relative shrink-0 w-11 h-11 rounded-md flex items-center justify-center"
                          style={{
                            border: "1px solid var(--color-border-dark)",
                            background: "var(--color-primary-50)",
                          }}
                        >
                          <div
                            className="absolute -inset-1.75 rounded-md animate-ping"
                            style={{
                              border: "1px solid var(--color-border-light)",
                              animationDuration: "2.8s",
                            }}
                          />
                          <Trophy
                            className="w-5 h-5"
                            style={{ color: "var(--color-primary)" }}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <span
                            className="text-sm font-bold uppercase"
                            style={{ color: "var(--color-primary)" }}
                          >
                            Challenge completed
                          </span>
                          <span
                            className="text-xs tracking-wide"
                            style={{ color: "var(--color-text-light-green)" }}
                          >
                            Great work — your team solved every question.
                          </span>
                        </div>
                      </div>

                      {/* right: badge */}
                      <div
                        className="flex items-center gap-2 rounded-md px-3.5 py-1.5 shrink-0"
                        style={{
                          border: "1px solid var(--color-border-medium)",
                          background: "var(--color-primary-50)",
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-md shrink-0"
                          style={{
                            background: "var(--color-primary)",
                            boxShadow: "0 0 6px var(--color-shadow-primary)",
                            animation: "pulse 2s ease-in-out infinite",
                          }}
                        />
                        <span
                          className="font text-xs font-semibold uppercase"
                          style={{ color: "var(--color-primary)" }}
                        >
                          All answers locked
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {mappingsModalOpen && (hasTksaData || hasMitreData) && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div
                      className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                      onClick={() => setMappingsModalOpen(false)}
                    />
                    <div className="relative z-10 w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden rounded-md border border-(--color-primary)/30 bg-(--color-bg-card) shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                      <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-(--color-primary)" />
                          <span className="text-sm font-semibold text-(--color-text-primary)">
                            TKSA Alignment and MITRE Mapping
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setMappingsModalOpen(false)}
                          aria-label="Close"
                          className="rounded-md p-1.5 text-(--color-text-muted) hover:bg-white/5 hover:text-(--color-primary) transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-5">
                        {/* Tab header */}
                        <div className="flex flex-wrap border-b border-(--color-primary)/15">
                          {hasTksaData && (
                            <button
                              type="button"
                              onClick={() => setFrameworkTab("tksa")}
                              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                activeFrameworkTab === "tksa"
                                  ? "text-(--color-primary) border-(--color-primary) bg-(--color-primary)/5"
                                  : "text-(--color-text-muted) border-transparent hover:text-(--color-text-primary) hover:bg-(--color-primary)/5"
                              }`}
                            >
                              <BarChart3 className="w-4 h-4" />
                              TKSA Alignment
                              <span className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md bg-(--color-primary)/10 text-(--color-text-light-green)">
                                {tksaBuckets.reduce(
                                  (s, b) => s + b.items.length,
                                  0,
                                )}
                              </span>
                            </button>
                          )}
                          {hasMitreData && (
                            <button
                              type="button"
                              onClick={() => setFrameworkTab("mitre")}
                              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                activeFrameworkTab === "mitre"
                                  ? "text-(--color-primary) border-(--color-primary) bg-(--color-primary)/5"
                                  : "text-(--color-text-muted) border-transparent hover:text-(--color-text-primary) hover:bg-(--color-primary)/5"
                              }`}
                            >
                              <ShieldAlert className="w-4 h-4" />
                              MITRE Mapping
                              <span className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md bg-(--color-primary)/10 text-(--color-text-light-green)">
                                {Object.values(mitreAttack)
                                  .filter(Array.isArray)
                                  .reduce((s, a) => s + a.length, 0)}
                              </span>
                            </button>
                          )}
                        </div>

                        {/* TKSA content */}
                        {activeFrameworkTab === "tksa" && hasTksaData && (
                          <div className="pt-4 flex flex-col gap-4">
                            {tksaBuckets.map((bucket) => {
                              const colorMap = {
                                Tasks: {
                                  dot: "#378ADD",
                                  bg: "rgba(55,138,221,0.15)",
                                  border: "rgba(55,138,221,0.3)",
                                  text: "#85B7EB",
                                },
                                Skills: {
                                  dot: "#639922",
                                  bg: "rgba(99,153,34,0.15)",
                                  border: "rgba(99,153,34,0.3)",
                                  text: "#97C459",
                                },
                                Abilities: {
                                  dot: "#7F77DD",
                                  bg: "rgba(127,119,221,0.15)",
                                  border: "rgba(127,119,221,0.3)",
                                  text: "#AFA9EC",
                                },
                                Knowledge: {
                                  dot: "#BA7517",
                                  bg: "rgba(186,117,23,0.15)",
                                  border: "rgba(186,117,23,0.3)",
                                  text: "#EF9F27",
                                },
                                Other: {
                                  dot: "#888888",
                                  bg: "rgba(128,128,128,0.15)",
                                  border: "rgba(128,128,128,0.3)",
                                  text: "#AAAAAA",
                                },
                              };
                              const c = colorMap[bucket.label] ?? {
                                dot: "#888",
                                bg: "rgba(128,128,128,0.15)",
                                border: "rgba(128,128,128,0.3)",
                                text: "#aaa",
                              };
                              return (
                                <div key={bucket.label}>
                                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-(--color-text-muted) mb-2 flex items-center gap-2">
                                    {bucket.label}
                                    <span className="font-mono tabular-nums">
                                      {bucket.items.length}
                                    </span>
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {bucket.items.map((item) => (
                                      <span
                                        key={item}
                                        className="inline-flex items-center gap-1.5 text-xs font-mono font-medium px-2.5 py-1 rounded-md border"
                                        style={{
                                          background: c.bg,
                                          color: c.text,
                                          borderColor: c.border,
                                        }}
                                      >
                                        <span
                                          className="w-1.5 h-1.5 rounded-full shrink-0"
                                          style={{ background: c.dot }}
                                        />
                                        {item}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* MITRE content — renders every array field so new
                            categories appear automatically. */}
                        {activeFrameworkTab === "mitre" && hasMitreData && (
                          <div className="pt-4 flex flex-col gap-4">
                            {Object.entries(mitreAttack)
                              .filter(
                                ([, items]) =>
                                  Array.isArray(items) && items.length > 0,
                              )
                              .map(([key, items], sectionIdx) => {
                                const palette = [
                                  {
                                    dot: "#7F77DD",
                                    bg: "rgba(127,119,221,0.12)",
                                    border: "rgba(127,119,221,0.3)",
                                    text: "#AFA9EC",
                                  },
                                  {
                                    dot: "#22C55E",
                                    bg: "rgba(34,197,94,0.12)",
                                    border: "rgba(34,197,94,0.3)",
                                    text: "#4ADE80",
                                  },
                                  {
                                    dot: "#378ADD",
                                    bg: "rgba(55,138,221,0.12)",
                                    border: "rgba(55,138,221,0.3)",
                                    text: "#85B7EB",
                                  },
                                  {
                                    dot: "#BA7517",
                                    bg: "rgba(186,117,23,0.12)",
                                    border: "rgba(186,117,23,0.3)",
                                    text: "#EF9F27",
                                  },
                                  {
                                    dot: "#EF4444",
                                    bg: "rgba(239,68,68,0.12)",
                                    border: "rgba(239,68,68,0.3)",
                                    text: "#FCA5A5",
                                  },
                                ];
                                const c = palette[sectionIdx % palette.length];
                                return (
                                  <div key={key}>
                                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-(--color-text-muted) mb-2">
                                      {key.replace(/[_-]/g, " ")}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      {items.map((item) => (
                                        <span
                                          key={item}
                                          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border"
                                          style={{
                                            background: c.bg,
                                            color: c.text,
                                            borderColor: c.border,
                                          }}
                                        >
                                          <span
                                            className="w-1.5 h-1.5 rounded-full shrink-0"
                                            style={{ background: c.dot }}
                                          />
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabbed Interface */}
        <div className="bg-transparent border border-(--color-primary)/30 rounded-md overflow-hidden">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-2 sm:px-6 py-3 sm:py-4 flex items-center justify-center gap-1.5 sm:gap-2 font-semibold transition-all relative ${
                    activeTab === tab.id
                      ? "text-(--color-primary) border-b-4 border-(--color-primary)"
                      : "bg-transparent text-(--color-text-muted) dark:text-gray-400 hover:bg-(--color-primary)/10 hover:text-(--color-text-primary) dark:hover:text-white border-b-2 border-transparent"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-3 sm:p-6 min-h-[50vh]">
            {activeTab === "play" && (
              <div className="space-y-6">
                <div className="border border-(--color-primary)/30 rounded-md p-3 sm:p-6 space-y-6">
                  {/* Machine status + attack box */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-(--color-primary)/10 rounded-md shrink-0">
                        <Monitor className="h-5 w-5 text-(--color-primary)" />
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-(--color-text-primary)">
                            Team Machine Status
                          </p>
                          <span className="px-3 py-1 rounded-md text-xs font-semibold bg-(--color-primary)/20 border border-(--color-primary)/40 text-(--color-primary)">
                            Running
                          </span>
                          {privateIp && (
                            <span className="px-3 py-1 text-xs text-(--color-text-muted) dark:text-gray-400 font-mono">
                              <b>SCENARIO_IP</b> {privateIp}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-(--color-text-muted) dark:text-gray-400 mt-1">
                          This is a shared team machine. Use VPN or Attack Box
                          to connect.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 sm:shrink-0">
                      <button
                        onClick={
                          directAttackBoxReady
                            ? handleOpenAttackBox
                            : handleStartAttackBox
                        }
                        disabled={attackBoxBusy || readOnly}
                        className="w-full sm:w-auto px-4 py-2 bg-(--color-primary)/10 border border-(--color-primary)/40 text-(--color-primary) font-semibold rounded-md hover:bg-(--color-primary)/20 hover:shadow-[0_0_15px_rgba(0,255,0,0.3)] transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-500/10 disabled:border-gray-500/40 disabled:text-gray-500 disabled:hover:shadow-none disabled:hover:bg-gray-500/10"
                      >
                        {directAttackBoxReady ? (
                          <ExternalLink className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        {attackBoxButtonLabel}
                      </button>
                    </div>
                  </div>

                  {attackBoxInlineMessage && (
                    <p
                      className={`text-xs ${
                        attackBoxUiPhase === ATTACKBOX_UI_PHASE.ERROR
                          ? "text-red-400"
                          : "text-(--color-text-muted) dark:text-gray-400"
                      }`}
                    >
                      {attackBoxInlineMessage}
                    </p>
                  )}

                  <div className="w-full flex flex-col sm:flex-row gap-3 sm:justify-end">
                    {canControlTeamScenario && !isTeamChallengeStopped && (
                      <button
                        onClick={handleStopTeamChallenge}
                        disabled={isTeamChallengeLoading}
                        className="w-full sm:w-auto px-6 py-3 bg-transparent border border-orange-500/50 text-orange-400 font-semibold rounded-md hover:bg-orange-500/10 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:border-gray-500/50 disabled:text-gray-500 disabled:hover:bg-transparent"
                      >
                        <Square className="h-4 w-4" />
                        {isTeamChallengeLoading
                          ? "Stopping..."
                          : "Stop Team Challenge"}
                      </button>
                    )}

                    {isTeamChallengeStopped && (
                      <span className="inline-flex items-center gap-2 px-6 py-3 text-sm text-(--color-text-muted) border border-(--color-border) rounded-md">
                        <ShieldAlert className="h-4 w-4" />
                        Team Challenge Stopped
                      </span>
                    )}

                    {directAttackBoxReady && (
                      <button
                        onClick={handleStopAttackBox}
                        disabled={isStoppingAttackBox || readOnly}
                        className="w-full sm:w-auto px-6 py-3 bg-transparent border border-red-500/50 text-red-400 font-semibold rounded-md hover:bg-red-500/10 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:border-gray-500/50 disabled:text-gray-500 disabled:hover:bg-transparent"
                      >
                        <Square className="h-4 w-4" />
                        {isStoppingAttackBox
                          ? "Stopping..."
                          : "Stop Attack Box"}
                      </button>
                    )}

                    <button
                      onClick={handleDownloadVpn}
                      disabled={isDownloadingVpn || readOnly}
                      className="w-full sm:w-auto px-6 py-3 bg-(--color-bg-card) dark:bg-black border border-(--color-border) text-(--color-text-primary) font-semibold rounded-md hover:border-(--color-primary) transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-900/30 disabled:border-gray-600/40 disabled:text-gray-600 disabled:hover:border-gray-600/40"
                    >
                      <Download className="h-4 w-4" />
                      {isDownloadingVpn
                        ? "Downloading..."
                        : "Connect using VPN"}
                    </button>
                  </div>
                </div>

                <TeamLabQuestionsSection
                  showQuestionsLoader={showQuestionsLoader}
                  totalQuestions={totalQuestions}
                  questions={questions}
                  answers={answers}
                  submitted={submitted}
                  submittingQuestions={submittingQuestions}
                  attempts={attempts}
                  setAnswers={setAnswers}
                  setSubmitted={setSubmitted}
                  handleSubmitAnswer={handleSubmitAnswer}
                  readOnly={readOnly}
                  onAttemptQuestion={(idx) => {
                    setAttemptedQuestions((prev) =>
                      prev[idx] ? prev : { ...prev, [idx]: true },
                    );
                  }}
                  missionTitle={labTitle}
                  missionDescription={labDescription}
                  difficulty={
                    scenario?.difficulty || preloadedLabData?.difficulty
                  }
                />
              </div>
            )}

            {activeTab === "walkthroughs" && (
              <LabWalkthroughTab
                isLoadingScenario={isLoadingScenario}
                isLocked={!allQuestionsAttempted}
                walkthroughMarkdown={walkthroughMarkdown}
                handleDownloadPDF={handleDownloadPDF}
                walkthroughRef={walkthroughRef}
              />
            )}

            {activeTab === "remediation" && (
              <LabRemediationTab
                isLoadingScenario={isLoadingScenario}
                isLocked={!allQuestionsAttempted}
                playbookMarkdown={playbookMarkdown}
                runbookMarkdown={runbookMarkdown}
                title={labTitle}
              />
            )}
          </div>
        </div>
      </div>
      {showAttackBoxLaunchModal && (
        <AttackBoxLaunchModal
          url={attackBoxUrl}
          onClose={() => setShowAttackBoxLaunchModal(false)}
          onOpen={handleLaunchAttackBoxUrl}
        />
      )}
      <ChatWidget scenarioId={chatScenarioId} />
    </PageLayout>
  );
}
